import { useState, useCallback, useRef } from 'react';
import { siteMapNodes, type SiteNode } from '@/mocks/websiteBuilder';
import { useTelemetry } from '../../../hooks/useTelemetry';
import { MODULES } from '../../../telemetry/constants/modules';
import { ACTIONS } from '../../../telemetry/constants/actions';

interface EditingModal {
  open: boolean;
  mode: 'add' | 'edit';
  nodeId?: string;
  parentId?: string;
  name: string;
  description: string;
}

interface ConfirmModal {
  open: boolean;
  nodeId: string;
  nodeName: string;
}

function generateId() {
  return 'page_' + Math.random().toString(36).slice(2, 9);
}

function flattenNodes(nodes: SiteNode[]): SiteNode[] {
  const result: SiteNode[] = [];
  for (const n of nodes) {
    result.push(n);
    if (n.children) {
      result.push(...flattenNodes(n.children));
    }
  }
  return result;
}

function findNodeById(nodes: SiteNode[], id: string): SiteNode | undefined {
  for (const n of nodes) {
    if (n.id === id) return n;
    if (n.children) {
      const found = findNodeById(n.children, id);
      if (found) return found;
    }
  }
  return undefined;
}

function removeNodeById(nodes: SiteNode[], id: string): SiteNode[] {
  return nodes
    .filter(n => n.id !== id)
    .map(n => ({
      ...n,
      children: n.children ? removeNodeById(n.children, id) : undefined,
    }));
}

function updateNodeById(
  nodes: SiteNode[],
  id: string,
  updater: (node: SiteNode) => SiteNode
): SiteNode[] {
  return nodes.map(n => {
    if (n.id === id) {
      return updater(n);
    }
    if (n.children) {
      return { ...n, children: updateNodeById(n.children, id, updater) };
    }
    return n;
  });
}

function insertChild(nodes: SiteNode[], parentId: string, child: SiteNode): SiteNode[] {
  return nodes.map(n => {
    if (n.id === parentId) {
      return { ...n, children: [...(n.children || []), child] };
    }
    if (n.children) {
      return { ...n, children: insertChild(n.children, parentId, child) };
    }
    return n;
  });
}

export interface SitemapGeneratorProps {
  nodes: SiteNode[];
  setNodes: React.Dispatch<React.SetStateAction<SiteNode[]>>;
  isExported?: boolean;
  onRequestUnlock?: () => void;
}

export default function SitemapGenerator({ nodes, setNodes, isExported, onRequestUnlock }: SitemapGeneratorProps) {
  const { trackAction } = useTelemetry({ module: MODULES.WEBSITE_BUILDER, submodule: 'Sitemap' });
  const [selectedNode, setSelectedNode] = useState<string>('home');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(
    new Set(nodes.filter(n => n.enabled).map(n => n.id))
  );

  const [editingModal, setEditingModal] = useState<EditingModal>({
    open: false,
    mode: 'add',
    name: '',
    description: '',
  });

  const [confirmModal, setConfirmModal] = useState<ConfirmModal>({
    open: false,
    nodeId: '',
    nodeName: '',
  });

  const nameInputRef = useRef<HTMLInputElement>(null);

  const checkLock = () => {
    if (isExported && onRequestUnlock) {
      onRequestUnlock();
      return true;
    }
    return false;
  };

  const toggleNode = useCallback((nodeId: string, parentId?: string) => {
    if (checkLock()) return;
    const nodeToToggle = findNodeById(nodes, nodeId);
    if (nodeToToggle) {
      trackAction(ACTIONS.EDIT_WEBSITE, 'Toggle Sitemap Page Visibility', {
        pageId: nodeId,
        pageName: nodeToToggle.name,
        enabled: !nodeToToggle.enabled
      });
    }
    setNodes(prev =>
      prev.map(node => {
        if (parentId && node.id === parentId && node.children) {
          return {
            ...node,
            children: node.children.map(child =>
              child.id === nodeId ? { ...child, enabled: !child.enabled } : child
            ),
          };
        }
        if (node.id === nodeId) {
          return { ...node, enabled: !node.enabled };
        }
        return node;
      })
    );
  }, [nodes, trackAction]);

  const toggleExpand = useCallback((nodeId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const moveNode = useCallback((nodeId: string, direction: 'up' | 'down', parentId?: string) => {
    if (checkLock()) return;
    setNodes(prev => {
      if (parentId) {
        return prev.map(p => {
          if (p.id === parentId && p.children) {
            const list = [...p.children];
            const idx = list.findIndex(c => c.id === nodeId);
            if (idx === -1) return p;
            const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
            if (targetIdx < 0 || targetIdx >= list.length) return p;
            const temp = list[idx];
            list[idx] = list[targetIdx];
            list[targetIdx] = temp;
            return { ...p, children: list };
          }
          return p;
        });
      }

      const list = [...prev];
      const idx = list.findIndex(n => n.id === nodeId);
      if (idx === -1) return prev;
      const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= list.length) return prev;
      const temp = list[idx];
      list[idx] = list[targetIdx];
      list[targetIdx] = temp;
      return list;
    });

    const node = findNodeById(nodes, nodeId);
    if (node) {
      trackAction(ACTIONS.EDIT_WEBSITE, 'Reorder Sitemap Page', {
        pageId: nodeId,
        pageName: node.name,
        direction,
      });
    }
  }, [checkLock, nodes, trackAction]);

  // Drag and drop state
  const [draggedNodeId, setDraggedNodeId] = useState<string | null>(null);
  const [draggedParentId, setDraggedParentId] = useState<string | undefined>(undefined);
  const [dragOverNodeId, setDragOverNodeId] = useState<string | null>(null);
  const [dropPosition, setDropPosition] = useState<'before' | 'after'>('after');

  const handleDragStart = (e: React.DragEvent, nodeId: string, parentId?: string) => {
    if (checkLock()) {
      e.preventDefault();
      return;
    }
    setDraggedNodeId(nodeId);
    setDraggedParentId(parentId);
    e.dataTransfer.setData('text/plain', nodeId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, targetNodeId: string, targetParentId?: string) => {
    if (!draggedNodeId || draggedNodeId === targetNodeId) return;
    if (draggedParentId !== targetParentId) return;

    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const isBefore = e.clientY < midY;
    setDragOverNodeId(targetNodeId);
    setDropPosition(isBefore ? 'before' : 'after');
  };

  const handleDragLeave = () => {
    setDragOverNodeId(null);
  };

  const handleDrop = (e: React.DragEvent, targetNodeId: string, targetParentId?: string) => {
    e.preventDefault();
    if (!draggedNodeId || draggedNodeId === targetNodeId || draggedParentId !== targetParentId) {
      setDraggedNodeId(null);
      setDragOverNodeId(null);
      return;
    }

    setNodes(prev => {
      if (targetParentId) {
        return prev.map(p => {
          if (p.id === targetParentId && p.children) {
            const list = [...p.children];
            const srcIdx = list.findIndex(c => c.id === draggedNodeId);
            if (srcIdx === -1) return p;
            const [item] = list.splice(srcIdx, 1);
            const dstIdx = list.findIndex(c => c.id === targetNodeId);
            const insertIdx = dropPosition === 'before' ? dstIdx : dstIdx + 1;
            list.splice(insertIdx, 0, item);
            return { ...p, children: list };
          }
          return p;
        });
      }

      const list = [...prev];
      const srcIdx = list.findIndex(n => n.id === draggedNodeId);
      if (srcIdx === -1) return prev;
      const [item] = list.splice(srcIdx, 1);
      const dstIdx = list.findIndex(n => n.id === targetNodeId);
      const insertIdx = dropPosition === 'before' ? dstIdx : dstIdx + 1;
      list.splice(insertIdx, 0, item);
      return list;
    });

    const node = findNodeById(nodes, draggedNodeId);
    if (node) {
      trackAction(ACTIONS.EDIT_WEBSITE, 'Drag Reorder Sitemap Page', {
        pageId: draggedNodeId,
        pageName: node.name,
      });
    }

    setDraggedNodeId(null);
    setDragOverNodeId(null);
  };

  const handleDragEnd = () => {
    setDraggedNodeId(null);
    setDragOverNodeId(null);
  };

  const openAddModal = useCallback((parentId?: string) => {
    if (checkLock()) return;
    const currentTotal = flattenNodes(nodes).length;
    if (currentTotal >= 50) return;
    setEditingModal({
      open: true,
      mode: 'add',
      parentId,
      name: '',
      description: '',
    });
    setTimeout(() => nameInputRef.current?.focus(), 50);
  }, [checkLock, nodes]);

  const openEditModal = useCallback((nodeId: string) => {
    if (checkLock()) return;
    const node = findNodeById(nodes, nodeId);
    if (!node) return;
    setEditingModal({
      open: true,
      mode: 'edit',
      nodeId,
      name: node.name,
      description: node.description,
    });
    setTimeout(() => nameInputRef.current?.focus(), 50);
  }, [checkLock, nodes]);

  const openAddChildModal = useCallback((parentId: string) => {
    if (checkLock()) return;
    const currentTotal = flattenNodes(nodes).length;
    if (currentTotal >= 50) return;
    setEditingModal({
      open: true,
      mode: 'add',
      parentId,
      name: '',
      description: '',
    });
    setTimeout(() => nameInputRef.current?.focus(), 50);
  }, [checkLock, nodes]);

  const closeEditingModal = useCallback(() => {
    setEditingModal(prev => ({ ...prev, open: false }));
  }, []);

  const saveEditingModal = useCallback(() => {
    const { mode, nodeId, parentId, name, description } = editingModal;
    const trimmedName = name.trim();
    if (!trimmedName) return;

    if (mode === 'edit' && nodeId) {
      trackAction(ACTIONS.EDIT_PAGE, 'Edit Sitemap Page Details', { pageId: nodeId, pageName: trimmedName });
      setNodes(prev =>
        updateNodeById(prev, nodeId, n => ({
          ...n,
          name: trimmedName,
          description: description.trim() || n.description,
        }))
      );
    } else {
      trackAction(ACTIONS.CREATE_PAGE, 'Add Page to Sitemap', { pageName: trimmedName, parentId: parentId || null });
      const newNode: SiteNode = {
        id: generateId(),
        name: trimmedName,
        description: description.trim() || 'Custom page added by user.',
        enabled: true,
        status: 'optional',
        children: [],
      };
      if (parentId) {
        setNodes(prev => insertChild(prev, parentId, newNode));
        setExpandedNodes(prev => {
          const next = new Set(prev);
          next.add(parentId);
          return next;
        });
      } else {
        setNodes(prev => {
          // If "contact" exists at the end, insert right before contact for optimal website flow
          const contactIdx = prev.findIndex(n => n.id === 'contact');
          if (contactIdx !== -1) {
            const nextList = [...prev];
            nextList.splice(contactIdx, 0, newNode);
            return nextList;
          }
          return [...prev, newNode];
        });
      }
      setSelectedNode(newNode.id);
    }
    closeEditingModal();
  }, [editingModal, closeEditingModal, trackAction]);

  const openDeleteModal = useCallback((nodeId: string) => {
    if (checkLock()) return;
    const node = findNodeById(nodes, nodeId);
    if (!node) return;
    setConfirmModal({ open: true, nodeId, nodeName: node.name });
  }, [checkLock, nodes]);

  const confirmDelete = useCallback(() => {
    trackAction(ACTIONS.DELETE_PAGE, 'Remove Page from Sitemap', { pageId: confirmModal.nodeId, pageName: confirmModal.nodeName });
    setNodes(prev => removeNodeById(prev, confirmModal.nodeId));
    if (selectedNode === confirmModal.nodeId) {
      setSelectedNode('home');
    }
    setConfirmModal({ open: false, nodeId: '', nodeName: '' });
  }, [confirmModal.nodeId, selectedNode, trackAction]);

  const closeDeleteModal = useCallback(() => {
    setConfirmModal({ open: false, nodeId: '', nodeName: '' });
  }, []);

  const selectedData = findNodeById(nodes, selectedNode);

  const allFlatNodes = flattenNodes(nodes);
  const activeCount = allFlatNodes.filter(n => n.enabled).length;
  const totalCount = allFlatNodes.length;

  const renderNode = (
    node: SiteNode,
    index: number,
    siblingsList: SiteNode[],
    level: number = 0,
    parentId?: string
  ) => {
    const isSelected = selectedNode === node.id;
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const canMoveUp = index > 0;
    const canMoveDown = index < siblingsList.length - 1;
    const isDraggingThis = draggedNodeId === node.id;
    const isDragOverThis = dragOverNodeId === node.id;

    return (
      <div
        key={node.id}
        className={`relative transition-all duration-150 ${isDraggingThis ? 'opacity-40 scale-[0.99]' : ''}`}
        onDragOver={(e) => handleDragOver(e, node.id, parentId)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, node.id, parentId)}
      >
        {/* Drop indicator lines */}
        {isDragOverThis && dropPosition === 'before' && (
          <div className="h-0.5 bg-[#0859b8] rounded-full my-0.5 mx-2 shadow-sm animate-pulse" />
        )}

        <div className="flex items-center gap-1.5 group/node">
          {/* Drag Handle */}
          <div
            draggable
            onDragStart={(e) => handleDragStart(e, node.id, parentId)}
            onDragEnd={handleDragEnd}
            className="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-[#0859b8] cursor-grab active:cursor-grabbing rounded hover:bg-gray-100 transition-colors flex-shrink-0"
            title="Drag to reorder"
          >
            <i className="ri-drag-move-2-line text-xs"></i>
          </div>

          {/* Expand toggle for nodes with children */}
          {hasChildren ? (
            <button
              onClick={() => toggleExpand(node.id)}
              className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-200/50 transition-colors cursor-pointer flex-shrink-0"
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              <i className={`${isExpanded ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'} text-gray-500 text-sm`}></i>
            </button>
          ) : (
            <div className="w-5 h-5 flex-shrink-0"></div>
          )}

          {/* Connector line for children */}
          {level > 0 && (
            <div className="flex items-center flex-shrink-0">
              <div className="w-3 h-px bg-[#d9e4ef]"></div>
            </div>
          )}

          {/* Sequence Badge */}
          {level === 0 && (
            <span className="text-[10px] font-mono text-gray-400 w-4 text-right flex-shrink-0 select-none">
              {index + 1}.
            </span>
          )}

          {/* Node Card */}
          <div
            onClick={() => setSelectedNode(node.id)}
            className={`relative rounded-lg px-3 py-2 cursor-pointer transition-all duration-150 flex-1 min-w-0 border ${
              isSelected
                ? `bg-[#f7fbff] border-[#bfd7f5] border-l-4 border-l-[#0859b8] shadow-sm`
                : node.enabled
                ? `${level > 0 ? 'bg-[#fafcfe] border-[#edf2f7]' : 'bg-white border-[#dfe8f3]'} hover:border-[#d6e6f7] hover:shadow-sm`
                : `${level > 0 ? 'bg-gray-50 border-gray-100' : 'bg-gray-50 border-gray-200'} opacity-60`
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`text-sm font-medium truncate ${isSelected ? 'text-[#0859b8]' : node.enabled ? 'text-gray-900' : 'text-gray-500'}`}>
                  {node.name}
                </span>
                {node.status === 'default' && node.id !== 'home' && node.id !== 'about' && node.id !== 'contact' && node.id !== 'blog' && (
                  <span className="text-[9px] px-1.5 py-0.5 bg-[#eef6f8] text-[#5b7f87] border border-[#d7ebef] rounded flex-shrink-0">default</span>
                )}
                {node.status === 'default' && (node.id === 'home' || node.id === 'about' || node.id === 'contact' || node.id === 'blog') && (
                  <span className="text-[9px] px-1.5 py-0.5 bg-[#fff7e8] text-[#b26a00] border border-[#f6ddb0] rounded flex-shrink-0 flex items-center gap-1">
                    <i className="ri-lock-line text-[8px]"></i>
                    required
                  </span>
                )}
                {node.status === 'optional' && (
                  <span className="text-[9px] px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded flex-shrink-0">custom</span>
                )}
              </div>

              <div className="flex items-center gap-0.5 flex-shrink-0">
                {/* Move Up Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    moveNode(node.id, 'up', parentId);
                  }}
                  disabled={!canMoveUp}
                  className={`w-6 h-6 flex items-center justify-center rounded transition-all duration-150 ${
                    canMoveUp
                      ? 'cursor-pointer text-gray-400 hover:text-[#0859b8] hover:bg-[#eef4fb]'
                      : 'text-gray-200 cursor-not-allowed opacity-30'
                  }`}
                  title={canMoveUp ? 'Move up in navigation sequence' : 'Already at top'}
                >
                  <i className="ri-arrow-up-s-line text-xs"></i>
                </button>

                {/* Move Down Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    moveNode(node.id, 'down', parentId);
                  }}
                  disabled={!canMoveDown}
                  className={`w-6 h-6 flex items-center justify-center rounded transition-all duration-150 ${
                    canMoveDown
                      ? 'cursor-pointer text-gray-400 hover:text-[#0859b8] hover:bg-[#eef4fb]'
                      : 'text-gray-200 cursor-not-allowed opacity-30'
                  }`}
                  title={canMoveDown ? 'Move down in navigation sequence' : 'Already at bottom'}
                >
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </button>

                {/* Add child button */}
                {(node.id === 'services' || node.id === 'products') && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openAddChildModal(node.id);
                    }}
                    disabled={totalCount >= 50}
                    className={`w-6 h-6 flex items-center justify-center rounded transition-all duration-150 ${
                      totalCount >= 50
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-400 hover:text-[#0859b8] hover:bg-[#eef4fb] cursor-pointer'
                    }`}
                    title="Add child page"
                  >
                    <i className="ri-add-circle-line text-xs"></i>
                  </button>
                )}

                {/* Edit button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditModal(node.id);
                  }}
                  className="w-6 h-6 flex items-center justify-center rounded transition-all duration-150 cursor-pointer text-gray-400 hover:text-[#0859b8] hover:bg-[#eef4fb]"
                  title="Edit page details"
                >
                  <i className="ri-pencil-line text-xs"></i>
                </button>

                {/* Delete button */}
                {node.id !== 'home' && node.id !== 'about' && node.id !== 'contact' && node.id !== 'blog' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteModal(node.id);
                    }}
                    className="w-6 h-6 flex items-center justify-center rounded transition-all duration-150 cursor-pointer text-gray-400 hover:text-red-500 hover:bg-red-50"
                    title="Delete page"
                  >
                    <i className="ri-delete-bin-line text-xs"></i>
                  </button>
                )}

                {/* Toggle visibility */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleNode(node.id, parentId);
                  }}
                  className={`w-6 h-6 flex items-center justify-center rounded transition-all duration-150 cursor-pointer ${
                    node.enabled
                      ? 'text-gray-400 hover:text-[#0859b8] hover:bg-[#eef4fb]'
                      : 'text-gray-300 hover:text-gray-500 hover:bg-gray-100'
                  }`}
                  title={node.enabled ? 'Hide page' : 'Show page'}
                >
                  <i className={`${node.enabled ? 'ri-eye-line' : 'ri-eye-off-line'} text-xs`}></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Drop indicator line after */}
        {isDragOverThis && dropPosition === 'after' && (
          <div className="h-0.5 bg-[#0859b8] rounded-full my-0.5 mx-2 shadow-sm animate-pulse" />
        )}

        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="ml-7 mt-2 flex flex-col gap-2 relative">
            {/* Vertical connector */}
            <div className="absolute left-2.5 top-0 bottom-2 w-px bg-[#d9e4ef]"></div>
            {node.children!.map((child, cIdx, cList) =>
              renderNode(child, cIdx, cList, level + 1, node.id)
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex gap-6 h-[calc(100vh-280px)] min-h-[500px]">
      {/* Sitemap Tree */}
      <div className="flex-1 bg-[#ffffff] border border-[#e7edf5] rounded-xl shadow-sm p-6 overflow-y-auto flex flex-col">
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <div className="flex items-center gap-2">
            <h3 className="font-serif text-lg text-ink">Site Structure</h3>
            <span className="text-[11px] px-2 py-0.5 bg-blue-50 text-[#0859b8] rounded-full font-medium">
              Reorderable
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500">
              {activeCount} of {totalCount} pages active <span className="text-gray-400">(max 50)</span>
            </span>
            <button
              onClick={() => totalCount < 50 && openAddModal()}
              disabled={totalCount >= 50}
              className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap shadow-sm ${
                totalCount >= 50
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-[#0859b8] text-white hover:bg-[#06489a] cursor-pointer shadow-[#0859b8]/20'
              }`}
              title={totalCount >= 50 ? 'Maximum 50 pages reached' : 'Add custom page'}
            >
              <i className="ri-add-line"></i>
              Add Page
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-2 flex-1">
          {nodes.map((node, index, list) => renderNode(node, index, list))}
        </div>

        <div className="mt-4 pt-3 border-t border-[#f1f5f9] flex items-center justify-between text-xs text-gray-500 italic flex-shrink-0">
          <span>
            Use the <strong>Up/Down arrows</strong> or <strong>drag handles</strong> to adjust page sequence in the sitemap.
          </span>
          <span className="text-[11px] not-italic text-gray-400">
            {50 - totalCount} slots remaining
          </span>
        </div>
      </div>

      {/* Right Panel - Page Details */}
      <div className="w-80 bg-white rounded-xl border border-[#dfe8f3] p-5 flex-shrink-0 overflow-y-auto shadow-sm">
        <h3 className="font-semibold text-[#222222] text-base mb-4">Page Details</h3>

        {selectedData && (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-[#6b7280] uppercase tracking-wide">Page Name</label>
              <p className="text-sm font-medium text-[#222222] mt-1">{selectedData.name}</p>
            </div>

            <div>
              <label className="text-xs font-medium text-[#6b7280] uppercase tracking-wide">Status</label>
              <div className="flex items-center gap-2 mt-1">
                <span className={`w-2 h-2 rounded-full ${selectedData.enabled ? 'bg-emerald-500' : 'bg-gray-400'}`}></span>
                <span className="text-sm text-gray-700">
                  {selectedData.enabled ? 'Active' : 'Hidden'}
                </span>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-[#6b7280] uppercase tracking-wide">Type</label>
              <span className={`inline-block text-xs px-2 py-1 rounded mt-1 ${
                selectedData.status === 'default'
                  ? 'bg-gray-100 text-gray-600'
                  : 'bg-amber-50 text-amber-700'
              }`}>
                {selectedData.status === 'default' ? 'Default page' : 'Custom page'}
              </span>
            </div>

            <div>
              <label className="text-xs font-medium text-[#6b7280] uppercase tracking-wide">Content Plan</label>
              <p className="text-sm text-gray-600 mt-1 leading-relaxed">{selectedData.description}</p>
            </div>

            {selectedData.children && selectedData.children.length > 0 && (
              <div>
                <label className="text-xs font-medium text-[#6b7280] uppercase tracking-wide">Child Pages</label>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {selectedData.children.map(child => (
                    <span
                      key={child.id}
                      className={`text-[10px] px-2 py-1 rounded ${
                        child.enabled
                          ? 'bg-[#0859b8]/5 text-[#0859b8]'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {child.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-4 mt-2 border-t border-[#f1f5f9]">
              <label className="text-xs font-medium text-[#6b7280] uppercase tracking-wide">Generated Sections</label>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {selectedData.id === 'home' && ['Hero', 'USP', 'Services', 'Gallery', 'Contact'].map(s => (
                  <span key={s} className="text-[10px] px-2 py-1 bg-[#e8f9fb] text-[#0b7f8f] border border-[#cdeff3] rounded">{s}</span>
                ))}
                {selectedData.id === 'about' && ['Hero', 'Story', 'Team'].map(s => (
                  <span key={s} className="text-[10px] px-2 py-1 bg-[#e8f9fb] text-[#0b7f8f] border border-[#cdeff3] rounded">{s}</span>
                ))}
                {selectedData.id === 'services' && ['Hero', 'Service Grid', 'Booking'].map(s => (
                  <span key={s} className="text-[10px] px-2 py-1 bg-[#e8f9fb] text-[#0b7f8f] border border-[#cdeff3] rounded">{s}</span>
                ))}
                {selectedData.id === 'products' && ['Hero', 'Product Grid', 'Cart'].map(s => (
                  <span key={s} className="text-[10px] px-2 py-1 bg-[#e8f9fb] text-[#0b7f8f] border border-[#cdeff3] rounded">{s}</span>
                ))}
                {selectedData.id === 'blog' && ['Hero', 'Post Grid', 'Subscribe'].map(s => (
                  <span key={s} className="text-[10px] px-2 py-1 bg-[#e8f9fb] text-[#0b7f8f] border border-[#cdeff3] rounded">{s}</span>
                ))}
                {selectedData.id === 'gallery' && ['Hero', 'Photo Grid', 'Lightbox'].map(s => (
                  <span key={s} className="text-[10px] px-2 py-1 bg-[#e8f9fb] text-[#0b7f8f] border border-[#cdeff3] rounded">{s}</span>
                ))}
                {selectedData.id === 'contact' && ['Hero', 'Form', 'Map'].map(s => (
                  <span key={s} className="text-[10px] px-2 py-1 bg-[#e8f9fb] text-[#0b7f8f] border border-[#cdeff3] rounded">{s}</span>
                ))}
                {selectedData.id.startsWith('s') && ['Service Detail', 'Pricing', 'CTA'].map(s => (
                  <span key={s} className="text-[10px] px-2 py-1 bg-[#e8f9fb] text-[#0b7f8f] border border-[#cdeff3] rounded">{s}</span>
                ))}
                {selectedData.id.startsWith('p') && ['Product Detail', 'Specs', 'Buy'].map(s => (
                  <span key={s} className="text-[10px] px-2 py-1 bg-[#e8f9fb] text-[#0b7f8f] border border-[#cdeff3] rounded">{s}</span>
                ))}
                {!['home','about','services','products','blog','gallery','contact'].includes(selectedData.id) &&
                 !selectedData.id.startsWith('s') && !selectedData.id.startsWith('p') && (
                  <span className="text-[10px] px-2 py-1 bg-gray-100 text-gray-500 rounded">Custom sections</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {editingModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl border border-[#dfe8f3] shadow-lg w-full max-w-md mx-4 overflow-hidden">
            <div className="px-5 py-4 border-b border-[#dfe8f3] flex items-center justify-between">
              <h4 className="font-serif text-base text-ink">
                {editingModal.mode === 'add' ? 'Add New Page' : 'Edit Page'}
              </h4>
              <button
                onClick={closeEditingModal}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">
                  Page Name <span className="text-red-400">*</span>
                </label>
                <input
                  ref={nameInputRef}
                  type="text"
                  value={editingModal.name}
                  onChange={(e) => setEditingModal(prev => ({ ...prev, name: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveEditingModal(); }}
                  placeholder="e.g. Pricing, FAQ, Careers"
                  className="w-full text-sm px-3 py-2.5 border border-[#dfe8f3] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0859b8]/30 focus:border-[#0859b8] transition-colors"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">
                  Description
                </label>
                <textarea
                  value={editingModal.description}
                  onChange={(e) => setEditingModal(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Briefly describe what this page will contain..."
                  rows={3}
                  maxLength={200}
                  className="w-full text-sm px-3 py-2.5 border border-[#dfe8f3] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0859b8]/30 focus:border-[#0859b8] transition-colors resize-none"
                />
                <p className="text-[10px] text-gray-400 mt-1 text-right">
                  {editingModal.description.length}/200
                </p>
              </div>
              {editingModal.mode === 'add' && editingModal.parentId && (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg">
                  <i className="ri-information-line text-amber-600 text-xs"></i>
                  <span className="text-xs text-amber-700">
                    This page will be added under <strong>{findNodeById(nodes, editingModal.parentId)?.name}</strong>
                  </span>
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-[#dfe8f3] flex items-center justify-end gap-2">
              <button
                onClick={closeEditingModal}
                className="text-sm font-medium px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={saveEditingModal}
                disabled={!editingModal.name.trim()}
                className="text-sm font-medium px-4 py-2 rounded-lg bg-[#0859b8] text-white hover:bg-[#0859b8]/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
              >
                {editingModal.mode === 'add' ? 'Add Page' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl border border-[#dfe8f3] shadow-lg w-full max-w-sm mx-4 overflow-hidden">
            <div className="px-5 py-5 text-center">
              <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-delete-bin-line text-red-500 text-lg"></i>
              </div>
              <h4 className="font-serif text-base text-ink mb-1">Delete Page?</h4>
              <p className="text-sm text-gray-500">
                Are you sure you want to remove <strong className="text-gray-700">{confirmModal.nodeName}</strong>? This action cannot be undone.
              </p>
            </div>
            <div className="px-5 py-4 border-t border-[#dfe8f3] flex items-center justify-center gap-3">
              <button
                onClick={closeDeleteModal}
                className="text-sm font-medium px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="text-sm font-medium px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Delete Page
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}